(() => {
  const scope = '#MainContent product-info[id^="MainProduct-"]';
  const mobile = matchMedia('(max-width: 767.98px)');
  const galleries = new WeakSet();
  const observed = new WeakSet();
  const entered = new WeakSet();
  const imageMotion = '.media-type-image motion-element[data-motion="zoom-out-sm"]';
  const replay = new IntersectionObserver((entries) => {
    entries.forEach(({ target: motion, isIntersecting }) => {
      if (isIntersecting) {
        entered.add(motion);
      } else if (entered.delete(motion) && mobile.matches && motion.isConnected &&
          !motion.hasAttribute('hold') && !FoxTheme.config.motionReduced &&
          !FoxTheme.config.disableAnimations) {
        motion._animating = false;
        motion.classList.remove('is-animated');
        motion.preInitialize();
        motion.connectedCallback();
      }
    });
  });

  function updateBar(swiper, pagination = swiper.pagination.el) {
    const count = swiper.pagination.bullets.length || 1;
    const index = Math.max(0, Math.min(swiper.snapIndex || 0, count - 1));
    pagination.style.setProperty('--ma-bar-width', `${100 / count}%`);
    pagination.style.setProperty('--ma-bar-offset', `${index * (swiper.rtlTranslate ? -100 : 100)}%`);
  }

  function prepareGallery(gallery) {
    if (galleries.has(gallery)) return;
    galleries.add(gallery);
    gallery.dataset.mediaItemGap = '0';
    gallery.style.setProperty('--media-item-gap', '0px');
    gallery.querySelector('.product__media-gallery-viewer-wrap')?.classList.remove(
      'slider-controls--grouped-mobile', 'slider-controls--grouped-always'
    );
    gallery.closest('product-info').querySelectorAll(
      '.product > motion-element.product__media-wrapper, .product > motion-element.product__info-wrapper'
    ).forEach((wrapper) => {
      wrapper.setAttribute('hold', '');
      wrapper.getAnimations().forEach((animation) => animation.cancel());
      wrapper.style.transform = 'none';
      wrapper.style.opacity = '1';
    });

    const configure = (options) => {
      options.spaceBetween = 0;
      options.pagination = { ...options.pagination, type: 'bullets', clickable: true };
      options.on = { ...options.on, paginationUpdate: updateBar };
    };
    const nativeOptions = gallery.setSliderOptions;
    gallery.setSliderOptions = function () {
      nativeOptions.call(this);
      configure(this.sliderOptions);
    };
    if (gallery.sliderOptions) configure(gallery.sliderOptions);
    const swiper = gallery.sliderInstance?.slider;
    if (swiper && !swiper.destroyed) {
      const rebuild = swiper.params.pagination.type !== 'bullets';
      if (rebuild) swiper.pagination.destroy();
      [swiper.params, swiper.originalParams].forEach((options) => {
        options.spaceBetween = 0;
        Object.assign(options.pagination, { type: 'bullets', clickable: true });
      });
      swiper.on('paginationUpdate', updateBar);
      if (rebuild) {
        swiper.pagination.init();
        swiper.pagination.render();
      }
      swiper.update();
      swiper.pagination.update();
    }

    const watchImages = () => gallery.querySelectorAll(imageMotion).forEach((motion) => {
      if (observed.has(motion)) return;
      observed.add(motion);
      replay.observe(motion);
    });
    watchImages();
    new MutationObserver(watchImages).observe(gallery, { childList: true, subtree: true });
  }

  function start() {
    Promise.all(['media-gallery', 'motion-element'].map((name) => customElements.whenDefined(name)))
      .then(() => document.querySelectorAll(`${scope} media-gallery`).forEach(prepareGallery));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
(() => {
  const selector = '#MainContent .section--product-tabs[data-id$="__product_tabs_XneBhD"] product-tabs';
  const connected = new WeakSet();

  function recentIds() {
    try {
      const current = document.querySelector('product-recently-viewed[data-product-id]')?.dataset.productId;
      const saved = JSON.parse(localStorage.getItem('sleektheme:recently-viewed') || '[]');
      return Array.isArray(saved)
        ? [...new Set(saved.map(String))].filter((id) => /^\d+$/.test(id) && id !== current).slice(0, 20)
        : [];
    } catch {
      return [];
    }
  }

  async function cardsFrom(path, parameters, cardSelector) {
    const url = new URL(`${window.Shopify?.routes?.root || '/'}${path}`, location.origin);
    Object.entries(parameters).forEach(([key, value]) => url.searchParams.set(key, value));
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Product tabs: ${response.status}`);
    const page = new DOMParser().parseFromString(await response.text(), 'text/html');
    const currentHandle = location.pathname.match(/\/products\/([^/]+)/)?.[1];
    return [...page.querySelectorAll(cardSelector)].filter((card) => {
      const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href');
      return href && new URL(href, location.origin).pathname.match(/\/products\/([^/]+)/)?.[1] !== currentHandle;
    }).slice(0, 8);
  }

  function fill(panel, cards) {
    const grids = [panel.querySelector('grid-list'), panel.querySelector('template')?.content.querySelector('grid-list')];
    grids.filter(Boolean).forEach((grid) => {
      const template = document.createElement('template');
      template.innerHTML = grid.outerHTML;
      const replacement = template.content.firstElementChild;
      replacement.tabIndex = 0;
      replacement.querySelector('.products-list').replaceChildren(...cards.map((card) => {
        const column = document.createElement('div');
        column.className = 'f-column';
        column.appendChild(card.cloneNode(true));
        return column;
      }));
      grid.replaceWith(replacement);
    });
  }

  function enableDragging(tabs) {
    let drag;
    let moved = false;
    tabs.addEventListener('pointerdown', (event) => {
      moved = false;
      const grid = event.target.closest('grid-list');
      if (event.pointerType !== 'mouse' || event.button !== 0 || !grid ||
          !matchMedia('(min-width: 768px)').matches ||
          event.target.closest('button, input, select, textarea') ||
          grid.scrollWidth <= grid.clientWidth) return;
      drag = { grid, id: event.pointerId, x: event.clientX, left: grid.scrollLeft };
    });
    tabs.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id || !(event.buttons & 1)) return;
      const distance = event.clientX - drag.x;
      if (!moved && Math.abs(distance) < 8) return;
      moved = true;
      drag.grid.setAttribute('data-ma-dragging', '');
      drag.grid.setPointerCapture(event.pointerId);
      drag.grid.scrollLeft = drag.left - distance;
      event.preventDefault();
    });
    function finish() {
      if (!drag) return;
      const { grid, id } = drag;
      drag = null;
      grid.removeAttribute('data-ma-dragging');
      if (grid.hasPointerCapture(id)) grid.releasePointerCapture(id);
    }
    ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((name) => tabs.addEventListener(name, finish));
    tabs.addEventListener('click', (event) => {
      if (!moved || event.detail === 0) return;
      moved = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    tabs.addEventListener('dragstart', (event) => {
      if (event.target.closest('grid-list')) event.preventDefault();
    });
  }

  function connect(root = document) {
    root.querySelectorAll(selector).forEach((tabs) => {
      if (connected.has(tabs)) return;
      const panels = tabs.querySelectorAll('[role="tabpanel"]');
      if (panels.length < 2) return;
      connected.add(tabs);
      enableDragging(tabs);
      const loaded = [null, null];
      const loading = [false, false];

      async function load(index) {
        if (index > 1) return;
        const ids = index === 1 ? recentIds() : [];
        const key = index === 1 ? ids.join(',') : 'best-selling';
        if (loading[index] || loaded[index] === key) return;
        loading[index] = true;
        panels[index].setAttribute('aria-busy', 'true');
        try {
          const cards = index === 0
            ? await cardsFrom('collections/all', {
              section_id: 'main-collection-product-grid', sort_by: 'best-selling'
            }, '#ProductGridContainer .product-card')
            : ids.length ? await cardsFrom('search', {
              section_id: 'recently-viewed-products', type: 'product',
              q: ids.map((id) => `id:${id}`).join(' OR ')
            }, 'recently-viewed-products .product-card') : [];
          fill(panels[index], cards);
          loaded[index] = key;
        } catch (error) {
          console.warn(error);
        } finally {
          loading[index] = false;
          panels[index].removeAttribute('aria-busy');
        }
      }

      tabs.addEventListener('tabChange', () => load(Number(tabs.selectedIndex)));
      window.addEventListener('pageshow', () => load(1));
      load(0);
      load(1);
    });
  }

  customElements.whenDefined('product-tabs').then(() => {
    connect();
    document.addEventListener('shopify:section:load', (event) => connect(event.target));
  });
})();
(() => {
  const selector = '.cart-drawer-button[aria-controls="CartDrawer"]';
  const isEmpty = () => !document.documentElement.classList.contains('cart-has-items');

  function updateCart() {
    const empty = isEmpty();
    document.querySelectorAll(selector).forEach(button => {
      if (empty) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('tabindex', '-1');
      } else {
        button.removeAttribute('aria-disabled');
        button.removeAttribute('tabindex');
      }
    });
    const drawer = document.getElementById('CartDrawer');
    if (empty && drawer?.open) drawer.hide();
  }

  function blockEmptyCart(event) {
    if (!isEmpty() || !event.target.closest?.(selector)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  document.addEventListener('click', blockEmptyCart, true);
  document.addEventListener('auxclick', blockEmptyCart, true);
  new MutationObserver(updateCart).observe(document.documentElement, {
    attributes: true, attributeFilter: ['class']
  });
  document.addEventListener('shopify:section:load', updateCart);
  updateCart();
})();

// Style Shopify's existing Apple Pay button; keep its native payment behavior.
(() => {
  const selector = 'product-info[id^="MainProduct-"] shopify-apple-pay-button';
  const styledButtons = new WeakSet();

  function styleButton(host) {
    // Shopify currently exposes its native button through this property.
    // Leave the native checkout unchanged if that implementation changes.
    const button = host.button;
    if (!(button instanceof HTMLButtonElement) ||
        button.getRootNode().host !== host ||
        !button.classList.contains('apple-pay-button') ||
        styledButtons.has(button)) return;

    const logo = button.querySelector('svg');
    if (!logo) return;

    const styles = {
      'background': '#fff',
      'color': '#000',
      'border': '2px solid #000',
      'box-sizing': 'border-box',
      'box-shadow': 'none'
    };
    Object.entries(styles).forEach(([property, value]) => {
      button.style.setProperty(property, value);
    });
    logo.style.setProperty('fill', '#000');
    button.querySelectorAll('.apple-pay--content > span').forEach((text) => {
      text.style.setProperty('display', 'none');
    });
    styledButtons.add(button);
  }

  function connect() {
    const main = document.getElementById('MainContent');
    if (!main) return;
    let scheduled = false;

    function scan() {
      scheduled = false;
      main.querySelectorAll(selector).forEach(styleButton);
    }

    function scheduleScan() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(scan);
    }

    // Shopify updates the host's light DOM after the native button is ready.
    new MutationObserver(scheduleScan).observe(main, { childList: true, subtree: true });
    customElements.whenDefined('shopify-apple-pay-button').then(scheduleScan);
    document.addEventListener('shopify:section:load', scheduleScan);
    window.addEventListener('pageshow', scheduleScan);
    scan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', connect, { once: true });
  } else {
    connect();
  }
})();